import PropTypes from 'prop-types';
import {batch, useSelector} from 'react-redux';
import {
  getMaxAspirateHighVolume,
  getMaxAspirateVolume,
  getMinAspirateHighVolume,
  getMinAspirateVolume,
  getRecommendedAspirateHighVolume,
} from 'stores/selectors/configSelectors';
import {getLastOpenedProtocol} from 'stores/selectors/protocolSelectors';
import {useIntl} from 'react-intl';
import {parseMessage} from '../../../../../lib/stringHelper';
import PipettingProfileInputType from '../PipettingProfileInputType';
import {TaskType, TrayType} from '../../../../../constants/dataEnum';
import {PipettingProfileItemWrapper} from '../PipettingProfileWrapper';
import PipettingItemMixingPosition from './PipettingItemMixingPosition';
import PipettingItemMixingSpeed from './PipettingItemMixingSpeed';

function PipettingItemMixingDetailGroup({
  onChangeVolume,
  onChangeCycle,
  onChangeMixingPosition,
  onChangeMixingPositionAspirate,
  onChangeMixingPositionDispense,
  onChangeMixingSpeed,
  task,
  hideMixingSpeed,
  showMixingVolume,
}) {
  const {formatMessage} = useIntl();
  const {mixingVolume, mixingCycle, type} = task;

  const lastOpenedProtocol = useSelector(getLastOpenedProtocol);
  const {trayType} = lastOpenedProtocol;
  const minVolume = useSelector(getMinAspirateVolume);
  const minHVVolume = useSelector(getMinAspirateHighVolume);
  const maxVolume = useSelector(getMaxAspirateVolume);
  const maxHVVolume = useSelector(getMaxAspirateHighVolume);
  const recommendedHVVolume = useSelector(getRecommendedAspirateHighVolume);
  const labelVolume = parseMessage('protocolEditor.taskList.pipettingProfile.volume');
  const labelCycle = parseMessage('protocolEditor.taskList.pipettingProfile.cycle');
  const isMixingVolumeBelowRecommended =
    trayType === TrayType.HIGH_VOLUME_CADDY && mixingVolume < recommendedHVVolume.value;

  const onBothMixingPositionChange = (...param) => {
    if (onChangeMixingPosition) {
      onChangeMixingPosition(...param);
      return;
    }

    batch(() => {
      onChangeMixingPositionAspirate(...param);
      onChangeMixingPositionDispense(...param);
    });
  };

  return (
    <div className={`pipetting-profile__item__mixing-detail-container ${task.enableMixing ? '' : ' disabled'}`}>
      <div className="pipetting-profile__item__mixing-detail-inline">
        {showMixingVolume ? (
          <PipettingProfileItemWrapper className="pipetting-profile__item__mixing-volume">
            {labelVolume}
            <PipettingProfileInputType.NumericTextBox
              value={mixingVolume}
              minValue={trayType === TrayType.HIGH_VOLUME_CADDY ? minHVVolume.value : minVolume.value}
              maxValue={trayType === TrayType.HIGH_VOLUME_CADDY ? maxHVVolume.value : maxVolume.value}
              onChange={(value) => onChangeVolume(value, type)}
              increment={0.1}
              precision={1}
              tooltipLabel={
                isMixingVolumeBelowRecommended &&
                formatMessage(
                  {id: 'protocolEditor.highCV.infoMixingPipettingParameter'},
                  {recommendedCapacity: recommendedHVVolume.value},
                )
              }
              className={isMixingVolumeBelowRecommended ? 'warning' : ''}
            />
          </PipettingProfileItemWrapper>
        ) : (
          ''
        )}
        <PipettingProfileItemWrapper
          className={`${showMixingVolume ? 'pipetting-profile__item__mixing-cycle' : 'pipetting-profile__item__mixing-cycle-only'}`}
        >
          {labelCycle}
          <PipettingProfileInputType.NumericTextBox
            value={mixingCycle}
            maxValue={999999999}
            minValue={1}
            increment={1}
            onChange={(value) => onChangeCycle(value, type)}
            allowDecimal={false}
          />
          <PipettingProfileInputType.Unit.Times />
        </PipettingProfileItemWrapper>
      </div>
      <PipettingItemMixingPosition task={task} onChange={onBothMixingPositionChange} mixingPositionType={null} />
      <PipettingItemMixingPosition
        task={task}
        onChange={onChangeMixingPositionAspirate}
        mixingPositionType={TaskType.ASPIRATE}
      />
      <PipettingItemMixingPosition
        task={task}
        onChange={onChangeMixingPositionDispense}
        mixingPositionType={TaskType.DISPENSE}
      />
      {!hideMixingSpeed && <PipettingItemMixingSpeed task={task} onChange={onChangeMixingSpeed} />}
    </div>
  );
}

PipettingItemMixingDetailGroup.propTypes = {
  onChangeVolume: PropTypes.func.isRequired,
  onChangeCycle: PropTypes.func.isRequired,
  onChangeMixingPositionAspirate: PropTypes.func.isRequired,
  onChangeMixingPositionDispense: PropTypes.func.isRequired,
  onChangeMixingSpeed: PropTypes.func.isRequired,
  onChangeMixingPosition: PropTypes.func.isRequired,
  task: PropTypes.string.isRequired,
  hideMixingSpeed: PropTypes.bool,
  showMixingVolume: PropTypes.bool,
};

PipettingItemMixingDetailGroup.defaultProps = {
  hideMixingSpeed: false,
  showMixingVolume: true,
};

export default PipettingItemMixingDetailGroup;
